import { useSelector } from "@tanstack/react-store";
import type { CellSelectionState, RowData } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";
import { getRowRangeEdges, getRowRangeSpan } from "../features/range/rangeKey";
import { getRowSelectionDisplay } from "../features/selection/rowSelection";
import { getFocusedAddress } from "../focus/navigation";
import type {
    DataGridActiveEditor,
    DataGridAtoms,
    DataGridCellAddress,
} from "../types";

const focusedColumnIdForRow = (ranges: CellSelectionState, rowId: string) => {
    const focused = getFocusedAddress(ranges);
    return focused?.rowId === rowId ? focused.columnId : null;
};

const highlightKeyForRow = (
    cells: readonly DataGridCellAddress[],
    rowId: string,
) =>
    cells
        .filter((cell) => cell.rowId === rowId)
        .map((cell) => cell.columnId)
        .join(" ");

/**
 * The table's current row for a rendered one. Rows memoize on `original`, so
 * after a data change an unchanged row keeps its old row object, whose display
 * index and cell selection no longer resolve.
 */
export const getLiveRow = <TData extends RowData>(
    row: DataGridRow<TData>,
): DataGridRow<TData> =>
    row.table.getPrePaginatedRowModel().rowsById[row.id] ?? row;

/**
 * Whether `row` is expanded, for cells. The row object stays the same while
 * it expands, so a compiled cell that read `row.getIsExpanded()` would keep
 * its first answer; this subscription re-reads on every expand change.
 */
export const useIsRowExpanded = <TData extends RowData>(
    row: DataGridRow<TData>,
) =>
    useSelector(row.table.atoms.expanded, () =>
        getLiveRow(row).getIsExpanded(),
    );

/**
 * Everything a row reads from the atoms, each as a primitive (or the same
 * atom object) so the row re-renders only when its own slice changes: moving
 * focus touches two rows. Ranges re-render a row only when its span or the
 * edges it holds change; `RangeOutline` draws them.
 */
export const useRowState = <TData extends RowData>({
    row,
    atoms,
    getActiveKey,
}: {
    row: DataGridRow<TData>;
    atoms: DataGridAtoms;
    getActiveKey: (row: TData) => string | null | undefined;
}) => {
    const { table } = row;
    const rowId = row.id;
    const rowKey = getActiveKey(row.original);
    const activeKey = rowKey === undefined ? rowId : rowKey;
    const focusedColumnId = useSelector(table.atoms.cellSelection, (ranges) =>
        focusedColumnIdForRow(ranges, rowId),
    );
    const rangeSpan = useSelector(table.atoms.cellSelection, () =>
        getRowRangeSpan(table, getLiveRow(row).getDisplayIndex()),
    );
    const rangeEdges = useSelector(table.atoms.cellSelection, () =>
        getRowRangeEdges(table, getLiveRow(row).getDisplayIndex()),
    );
    const hasRangeAction = useSelector(
        atoms.rangeAction,
        (action) => action !== null,
    );
    const isListCellFocused = useSelector(
        atoms.listCellFocus,
        (listCellFocus) => listCellFocus && focusedColumnId !== null,
    );
    const selection = useSelector(table.atoms.rowSelection, () =>
        getRowSelectionDisplay(getLiveRow(row)),
    );
    const isActive = useSelector(
        atoms.activeRowKey,
        (activeRowKey) => activeKey !== null && activeRowKey === activeKey,
    );
    const editor = useSelector(
        atoms.activeEditor,
        (active): DataGridActiveEditor | null =>
            active?.rowId === rowId ? active : null,
    );
    const highlightKey = useSelector(atoms.highlights, (cells) =>
        highlightKeyForRow(cells, rowId),
    );
    return {
        focusedColumnId,
        /** The row's range cells, for `isInRangeSpan`. */
        rangeSpan,
        /** The range edges the row holds (`getRowRangeEdges`). */
        rangeEdges,
        hasRangeAction,
        /** List editing: one of this row's cells holds focus, not the row. */
        isListCellFocused,
        selection,
        isActive,
        editor,
        highlightKey,
    };
};
